/**
 * Registry of (page, key) CMS sections cleaning-app already consumes with a known,
 * fixed field shape — used to render friendly per-field forms instead of a raw-JSON
 * textarea. Anything not listed here still falls back to raw JSON (see cms/page.tsx).
 *
 * Two contentJson conventions exist in cleaning-app's CMS consumers, captured here as
 * `wrap`:
 *  - home (`homeServer.ts`'s `applySection`): `contentJson` IS the section's value —
 *    `merged[section.key] = JSON.parse(section.contentJson)`. wrap = false.
 *  - about/contact (`cmsServer.ts`'s `cmsSectionsToRecord`): sections are merged with
 *    `Object.assign`, so `contentJson` must itself be `{ [section.key]: value }`.
 *    wrap = true.
 */

export type ScalarFieldSpec = {
  kind: 'text' | 'textarea' | 'number' | 'boolean'
  key: string
  label: string
  placeholder?: string
}

export type ListFieldSpec = {
  kind: 'list'
  key: string
  label: string
  itemLabel: string
  itemFields: ScalarFieldSpec[]
}

export type WeekHoursFieldSpec = {
  kind: 'weekHours'
  key: string
  label: string
}

export type FieldSpec = ScalarFieldSpec | ListFieldSpec | WeekHoursFieldSpec

export interface KnownSectionSpec {
  /** The CMS section `key` this spec applies to (matches the registry key below). */
  key: string
  /** Human label shown in the modal header. */
  label: string
  /** true: contentJson = `{ [key]: value }`. false: contentJson = value directly. */
  wrap: boolean
  /**
   * 'single': exactly one field IS the whole value (fields[0].key === key).
   * 'object': the value is an object built from every field in `fields`.
   */
  shape: 'single' | 'object'
  fields: FieldSpec[]
}

export const WEEK_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const

export const KNOWN_SECTIONS: Record<string, Record<string, KnownSectionSpec>> = {
  home: {
    // Visibility/order for every home block is handled by the CMS page itself (Show/Hide + arrows); these forms are for blocks that also
    // carry text.
    'category-circles': {
      key: 'category-circles',
      label: 'Shop by category (round tiles)',
      wrap: false,
      shape: 'object',
      fields: [{ kind: 'text', key: 'title', label: 'Heading', placeholder: 'Shop by category' }],
    },
    hero: {
      key: 'hero',
      label: 'Hero',
      wrap: false,
      shape: 'object',
      fields: [
        { kind: 'text', key: 'title', label: 'Title' },
        { kind: 'textarea', key: 'subtitle', label: 'Subtitle' },
        { kind: 'text', key: 'primaryCta', label: 'Primary button text', placeholder: 'Book Now' },
        { kind: 'text', key: 'secondaryCta', label: 'Secondary button text', placeholder: 'Our Services' },
        { kind: 'text', key: 'backgroundImage', label: 'Background image URL' },
        {
          kind: 'list',
          key: 'stats',
          label: 'Hero stats',
          itemLabel: 'Stat',
          itemFields: [
            { kind: 'text', key: 'value', label: 'Value', placeholder: '500+' },
            { kind: 'text', key: 'label', label: 'Label', placeholder: 'Happy Clients' },
          ],
        },
      ],
    },
    stats: {
      key: 'stats',
      label: 'Stats',
      wrap: false,
      shape: 'object',
      fields: [
        { kind: 'text', key: 'title', label: 'Title' },
        { kind: 'textarea', key: 'subtitle', label: 'Subtitle' },
        {
          kind: 'list',
          key: 'statItems',
          label: 'Stat items',
          itemLabel: 'Stat',
          itemFields: [
            { kind: 'text', key: 'value', label: 'Value', placeholder: '500+' },
            { kind: 'text', key: 'label', label: 'Label', placeholder: 'Happy Clients' },
            { kind: 'text', key: 'icon', label: 'Icon (emoji)', placeholder: '😊' },
          ],
        },
      ],
    },
    testimonials: {
      key: 'testimonials',
      label: 'Testimonials',
      wrap: false,
      shape: 'object',
      fields: [
        { kind: 'text', key: 'title', label: 'Title' },
        { kind: 'textarea', key: 'subtitle', label: 'Subtitle' },
        {
          kind: 'list',
          key: 'testimonials',
          label: 'Testimonials',
          itemLabel: 'Testimonial',
          itemFields: [
            { kind: 'text', key: 'name', label: 'Name' },
            { kind: 'text', key: 'location', label: 'Location' },
            { kind: 'number', key: 'rating', label: 'Rating (1-5)' },
            { kind: 'textarea', key: 'text', label: 'Quote' },
            { kind: 'text', key: 'image', label: 'Avatar image URL' },
          ],
        },
      ],
    },
    whyUs: {
      key: 'whyUs',
      label: 'Why Choose Us',
      wrap: false,
      shape: 'object',
      fields: [
        { kind: 'text', key: 'title', label: 'Title' },
        { kind: 'textarea', key: 'subtitle', label: 'Subtitle' },
        {
          kind: 'list',
          key: 'reasons',
          label: 'Reasons',
          itemLabel: 'Reason',
          itemFields: [
            { kind: 'text', key: 'icon', label: 'Icon (emoji)' },
            { kind: 'text', key: 'title', label: 'Title' },
            { kind: 'textarea', key: 'description', label: 'Description' },
          ],
        },
      ],
    },
  },

  about: {
    intro: {
      key: 'intro',
      label: 'Introduction',
      wrap: false,
      shape: 'object',
      fields: [
        { kind: 'text', key: 'title', label: 'Page heading', placeholder: 'About Kinmel (leave blank for the default)' },
        { kind: 'textarea', key: 'body', label: 'Introduction text', placeholder: 'Leave blank to use Site Settings → Description' },
      ],
    },
    // The section KEY is "mission" but the text is stored under `missionStatement` — the spec key must be the section key or the friendly
    // form never matches the row (and edits silently fell through to raw JSON).
    mission: {
      key: 'mission',
      label: 'Mission Statement',
      wrap: true,
      shape: 'single',
      fields: [{ kind: 'textarea', key: 'missionStatement', label: 'Mission statement' }],
    },
    story: {
      key: 'story',
      label: 'Our Story',
      wrap: true,
      shape: 'single',
      fields: [{ kind: 'textarea', key: 'story', label: 'Story' }],
    },
    team: {
      key: 'team',
      label: 'Team Description',
      wrap: true,
      shape: 'single',
      fields: [{ kind: 'textarea', key: 'teamDescription', label: 'Team description' }],
    },
    stats: {
      key: 'stats',
      label: 'Stats',
      wrap: true,
      shape: 'single',
      fields: [
        {
          kind: 'list',
          key: 'stats',
          label: 'Stats',
          itemLabel: 'Stat',
          itemFields: [
            { kind: 'text', key: 'icon', label: 'Icon (name, see ServiceIcon)' },
            { kind: 'text', key: 'label', label: 'Label' },
            { kind: 'text', key: 'value', label: 'Value' },
          ],
        },
      ],
    },
    values: {
      key: 'values',
      label: 'Values',
      wrap: true,
      shape: 'single',
      fields: [
        {
          kind: 'list',
          key: 'values',
          label: 'Values',
          itemLabel: 'Value',
          itemFields: [
            { kind: 'text', key: 'icon', label: 'Icon (name, see ServiceIcon)' },
            { kind: 'text', key: 'title', label: 'Title' },
            { kind: 'textarea', key: 'description', label: 'Description' },
          ],
        },
      ],
    },
  },

  contact: {
    details: {
      key: 'details',
      label: 'Contact details',
      wrap: false,
      shape: 'object',
      fields: [
        { kind: 'text', key: 'subtitle', label: 'Page subtitle' },
        { kind: 'text', key: 'phone', label: 'Phone (blank = Site Settings phone)' },
        { kind: 'text', key: 'email', label: 'E-mail (blank = Site Settings e-mail)' },
        { kind: 'textarea', key: 'address', label: 'Address' },
      ],
    },
    form: {
      key: 'form',
      label: 'Message form',
      wrap: false,
      shape: 'object',
      fields: [
        { kind: 'text', key: 'title', label: 'Heading' },
        { kind: 'text', key: 'subtitle', label: 'Sub-heading' },
      ],
    },
    hours: {
      key: 'hours',
      label: 'Business Hours',
      wrap: true,
      shape: 'single',
      fields: [{ kind: 'weekHours', key: 'hours', label: 'Hours' }],
    },
    socialLinks: {
      key: 'socialLinks',
      label: 'Social Links',
      wrap: true,
      shape: 'single',
      fields: [
        {
          kind: 'list',
          key: 'socialLinks',
          label: 'Social links',
          itemLabel: 'Link',
          itemFields: [
            { kind: 'text', key: 'platform', label: 'Platform', placeholder: 'facebook' },
            { kind: 'text', key: 'url', label: 'URL', placeholder: 'https://…' },
            { kind: 'text', key: 'icon', label: 'Icon (optional)' },
          ],
        },
      ],
    },
    subtitle: {
      key: 'subtitle',
      label: 'Subtitle',
      wrap: true,
      shape: 'single',
      fields: [{ kind: 'text', key: 'subtitle', label: 'Subtitle' }],
    },
    phone: {
      key: 'phone',
      label: 'Phone',
      wrap: true,
      shape: 'single',
      fields: [{ kind: 'text', key: 'phone', label: 'Phone number' }],
    },
    email: {
      key: 'email',
      label: 'Email',
      wrap: true,
      shape: 'single',
      fields: [{ kind: 'text', key: 'email', label: 'Email address' }],
    },
    address: {
      key: 'address',
      label: 'Address',
      wrap: true,
      shape: 'single',
      fields: [{ kind: 'textarea', key: 'address', label: 'Address' }],
    },
  },

  layout: {
    header: {
      key: 'header',
      label: 'Header menu',
      wrap: false,
      shape: 'object',
      fields: [
        {
          kind: 'list',
          key: 'links',
          label: 'Menu links (in order)',
          itemLabel: 'Link',
          itemFields: [
            { kind: 'text', key: 'label', label: 'Label', placeholder: 'Shop' },
            { kind: 'text', key: 'href', label: 'Link', placeholder: '/products  (use /specials for the on-sale shortcut)' },
          ],
        },
      ],
    },
    footer: {
      key: 'footer',
      label: 'Footer',
      wrap: false,
      shape: 'object',
      fields: [
        { kind: 'textarea', key: 'tagline', label: 'Footer text (blank = Site Settings → Description)' },
        {
          kind: 'list',
          key: 'links',
          label: 'Footer links ("More" column)',
          itemLabel: 'Link',
          itemFields: [
            { kind: 'text', key: 'label', label: 'Label', placeholder: 'Track your order' },
            { kind: 'text', key: 'href', label: 'Link', placeholder: '/track' },
          ],
        },
        { kind: 'boolean', key: 'showSocial', label: 'Show social media icons (links come from Site Settings → Social Links)' },
        { kind: 'text', key: 'copyright', label: 'Copyright line (blank = © year Brand. All rights reserved.)' },
      ],
    },
  },
}

/** Case-insensitive lookup: CmsController.cs unconditionally lowercases both Page and Key on
 * every create/update (`body.Key.Trim().ToLowerInvariant()`), so any registry key containing
 * an uppercase letter (e.g. "whyUs", "missionStatement") can never exact-match what's actually
 * stored/returned — without this, editing those real sections silently falls back to the raw
 * JSON textarea instead of the friendly form. */
export function getKnownSectionSpec(page: string, key: string): KnownSectionSpec | undefined {
  const pageSpecs = KNOWN_SECTIONS[page.toLowerCase()] ?? KNOWN_SECTIONS[page]
  if (!pageSpecs) return undefined
  if (pageSpecs[key]) return pageSpecs[key]
  const lowerKey = key.toLowerCase()
  return Object.values(pageSpecs).find((spec) => spec.key.toLowerCase() === lowerKey)
}

function normalizeScalar(field: ScalarFieldSpec, raw: unknown): string | number | boolean {
  if (field.kind === 'boolean') return raw !== false && raw !== 'no' && raw !== 'false' && raw !== 0 && raw !== '' // default ON when unset
  if (field.kind === 'number') return typeof raw === 'number' ? raw : Number(raw) || 0
  return typeof raw === 'string' ? raw : raw == null ? '' : String(raw)
}

function normalizeListItems(field: ListFieldSpec, raw: unknown): Record<string, string | number | boolean>[] {
  if (!Array.isArray(raw)) return []
  return raw.map((item) => {
    const out: Record<string, string | number | boolean> = {}
    for (const f of field.itemFields) out[f.key] = normalizeScalar(f, (item as Record<string, unknown>)?.[f.key])
    return out
  })
}

function normalizeWeekHours(raw: unknown): Record<string, string> {
  const source = (raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}) ?? {}
  const out: Record<string, string> = {}
  for (const day of WEEK_DAYS) out[day] = typeof source[day] === 'string' ? (source[day] as string) : ''
  return out
}

function normalizeField(field: FieldSpec, raw: unknown): unknown {
  if (field.kind === 'list') return normalizeListItems(field, raw)
  if (field.kind === 'weekHours') return normalizeWeekHours(raw)
  return normalizeScalar(field, raw)
}

/** contentJson -> flat form state keyed by field.key (ready for the friendly form's useState). */
export function parseKnownSection(spec: KnownSectionSpec, contentJson: string | undefined): Record<string, unknown> {
  let root: unknown
  try {
    root = contentJson ? JSON.parse(contentJson) : {}
  } catch {
    root = {}
  }

  if (spec.shape === 'single') {
    const field = spec.fields[0]
    const raw = spec.wrap ? (root as Record<string, unknown>)?.[field.key] : root
    return { [field.key]: normalizeField(field, raw) }
  }

  const source = (spec.wrap ? (root as Record<string, unknown>)?.[spec.key] : root) as
    | Record<string, unknown>
    | undefined
  const state: Record<string, unknown> = {}
  for (const field of spec.fields) state[field.key] = normalizeField(field, source?.[field.key])
  return state
}

/** Form state -> the exact contentJson string to persist. */
export function serializeKnownSection(spec: KnownSectionSpec, state: Record<string, unknown>): string {
  const value =
    spec.shape === 'single'
      ? normalizeField(spec.fields[0], state[spec.fields[0].key])
      : Object.fromEntries(spec.fields.map((f) => [f.key, normalizeField(f, state[f.key])]))

  return JSON.stringify(spec.wrap ? { [spec.key]: value } : value)
}
